import { useState, useEffect } from 'react'
import { Row, Col, Tag, message, Popover } from 'antd'
import AddTag from './Add'
import {
  type ModuleSummaryQuery_module_Module_tags_ModuleTag,
  type ModuleSummaryQuery_module_Module_vehicle_Vehicle,
} from '~/gql/generated/graphql'
import { ModuleTagName } from '../../../__generated__/globalTypes'
import {
  useMissing,
  useLocated,
  useProviderDropOff,
  useMaintenancePickUp,
  useMaintenanceStart,
  useMaintenanceEnd,
  useLowBatteryOverrideStart,
  useLowBatteryOverrideEnd,
} from '~/hooks'
import { formatDatetime } from '~/utils'
import { useApolloClient } from '@apollo/client'
import useEventStart from '~/hooks/useEventStart'
import useEventEnd from '~/hooks/useEventEnd'

type Props = {
  vehicleId: ModuleSummaryQuery_module_Module_vehicle_Vehicle['id']
  tags: ModuleSummaryQuery_module_Module_tags_ModuleTag[]
}

export default ({ vehicleId, tags }: Props) => {
  const [savedTags, updateSavedTags] = useState<
    Pick<
      ModuleSummaryQuery_module_Module_tags_ModuleTag,
      'name' | 'created_at'
    >[]
  >([])
  const [missing] = useMissing({ vehicleId })
  const [located] = useLocated({ vehicleId })
  const [maintenancePickUp] = useMaintenancePickUp({ vehicleId })
  const [providerDropOff] = useProviderDropOff({ vehicleId })
  const [maintenanceStart] = useMaintenanceStart({ vehicleId })
  const [maintenanceEnd] = useMaintenanceEnd({ vehicleId })
  const [lowBatteryOverrideStart] = useLowBatteryOverrideStart({ vehicleId })
  const [lowBatteryOverrideEnd] = useLowBatteryOverrideEnd({ vehicleId })
  const [eventStart] = useEventStart({ vehicleId })
  const [eventEnd] = useEventEnd({ vehicleId })
  const client = useApolloClient()

  useEffect(() => {
    updateSavedTags(tags.map(({ name, created_at }) => ({ name, created_at })))
  }, [tags])

  const refetch = async () =>
    await client.refetchQueries({
      include: ['ModuleSummary'],
    })

  const handleTagAdd = async (name: ModuleTagName) => {
    try {
      switch (name) {
        case ModuleTagName.missing:
          await missing()
          break
        case ModuleTagName.workshop:
          await maintenancePickUp()
          break
        case ModuleTagName.maintenance:
          await maintenanceStart()
          break
        case ModuleTagName.low_battery_override:
          await lowBatteryOverrideStart()
          break
        case ModuleTagName.event:
          await eventStart()
          break
        default:
          throw Error(`Invalid tag name: ${name}`)
      }
      await refetch()
      message.success('Tag added')
    } catch (err) {
      message.error(
        `Something went wrong when trying to add a tag: ${err.message}`,
      )
    }
  }

  const handleTagRemove = (name: ModuleTagName) => async () => {
    try {
      switch (name) {
        case ModuleTagName.missing:
          await located()
          break
        case ModuleTagName.workshop:
          await providerDropOff()
          break
        case ModuleTagName.maintenance:
          await maintenanceEnd()
          break
        case ModuleTagName.low_battery_override:
          await lowBatteryOverrideEnd()
          break
        case ModuleTagName.event:
          await eventEnd()
          break
        default:
          throw Error(`Invalid tag name: ${name}`)
      }
      await refetch()
      message.success('Tag removed.')
    } catch (err) {
      message.error(
        `Something went wrong when trying to remove tag: ${err.message}`,
      )
    }
  }

  return (
    <Row justify="start">
      <Col>
        {savedTags.length !== 0 &&
          savedTags.map(({ name: tagName, created_at }) => (
            <Popover
              key={tagName}
              content={formatDatetime(created_at)}
              placement="bottom"
            >
              <Tag
                closable={tagName !== 'vandalism'}
                onClose={handleTagRemove(tagName)}
                color="blue"
                style={{
                  border: 'none',
                  borderRadius: 24,
                  padding: '4px 14px',
                  fontFamily: 'Hellix-SemiBold',
                  cursor: 'default',
                }}
              >
                {tagName}
              </Tag>
            </Popover>
          ))}
      </Col>
      <Col>
        <AddTag
          tags={savedTags.map(({ name }) => name)}
          onComplete={handleTagAdd}
        />
      </Col>
    </Row>
  )
}
